'use client';

// src/components/cart/CartView.tsx

import Image from 'next/image';
import Link from 'next/link';
import { useMemo } from 'react';

import { books } from '@/lib/data';
import { getBookCover } from '@/lib/book-image';

import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCart, type CartItem } from '@/components/cart/cart-context';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

function formatMoney(value: number, currency: 'EUR' | 'USD') {
 return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(value);
}

export default function CartView() {
 const { state, dispatch } = useCart();
 const items = state.items;

 const { subtotal, shipping, total } = useMemo(() => {
 const s = items.reduce<number>((acc, it: CartItem) => acc + it.price * it.quantity, 0);
 const ship = 0; // digital goods -> 0 (set to >0 for physical)
 return { subtotal: s, shipping: ship, total: s + ship };
 }, [items]);
 const currency = items[0]?.currency || 'USD';
 const mixedCurrency = items.some((item) => (item.currency || currency) !== currency);
 const missingFormat = items.some((item) => !item.digitalFormat);

 if (!items.length) {
 return (
 <div className="rounded-lg bg-blush/30 p-8 text-center">
 <p className="text-charcoal/80">Your cart is empty.</p>
 <div className="mt-4">
 <Button asChild className="bg-terra text-white hover:bg-terra/90">
 <Link href="/shop">Browse the Shop</Link>
 </Button>
 </div>
 </div>
 );
 }

 return (
 <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_360px]">
 {/* Items */}
 <div className="space-y-6">
 <ul className="divide-y divide-blush/50 rounded-lg border border-blush/60 bg-white">
 {items.map((item: CartItem) => {
 const book = books.find((b) => b.id === item.id);
 const cover = book ? getBookCover(book.coverImageId, book.coverImageUrl, book.coverImageAlt, item.title) : null;
 const coverUrl = item.coverUrl || cover?.src;
 const coverAlt = item.title || cover?.alt || 'Book cover';
 const itemHref = item.routeHref || `/shop/${item.slug}`;

 return (
 <li key={item.id} className="flex flex-col gap-4 p-4 sm:flex-row sm:items-center">
 <div className="h-28 w-20 flex-shrink-0 overflow-hidden rounded-md bg-blush/40">
 {coverUrl ? (
 <Image
 src={coverUrl}
 alt={coverAlt}
 width={80}
 height={112}
 className="h-full w-full object-cover"
 onError={(event) => {
 event.currentTarget.style.display = 'none';
 }}
 />
 ) : null}
 </div>

 <div className="flex-1">
 <Link href={itemHref} className="font-medium text-charcoal hover:underline">
 {item.title}
 </Link>
 {item.itemType === 'bundle' ? <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-terra">Bundle{item.includedTitleIds?.length ? ` - ${item.includedTitleIds.length} books` : ''}</p> : null}
 {item.digitalFormat ? <p className="mt-1 text-xs font-semibold uppercase tracking-[0.16em] text-[#5F8769]">{item.digitalFormat.toUpperCase()} download</p> : <p className="mt-1 text-xs font-semibold text-red-700">Format required</p>}
 <div className="mt-1 text-sm text-charcoal/70">{formatMoney(item.price, item.currency || currency)}</div>
 {item.digitalSaleState === 'preorder' ? <p className="text-sm text-amber-900">Pre-order — charged now; delivery after owner release.</p> : null}
 {item.availableDigitalFormats?.length ? (
 <fieldset className="mt-3">
 <legend className="text-xs font-semibold text-charcoal/70">Download format</legend>
 <RadioGroup value={item.digitalFormat ?? ''} onValueChange={(value) => dispatch({ type: 'SET_FORMAT', id: item.id, digitalFormat: value as 'epub' | 'pdf' })} className="mt-2 flex flex-wrap gap-3">
 {item.availableDigitalFormats.map((format) => <Label key={format} htmlFor={`cart-${item.id}-${format}`} className="flex cursor-pointer items-center gap-2 rounded-lg border border-blush/70 bg-cream px-3 py-2 text-xs font-semibold"><RadioGroupItem id={`cart-${item.id}-${format}`} value={format} />{format.toUpperCase()}</Label>)}
 </RadioGroup>
 </fieldset>
 ) : null}
 </div>

 <div className="flex items-center justify-between gap-3 sm:w-64">
 <div className="text-right font-medium text-charcoal">
 {formatMoney(item.price * item.quantity, item.currency || currency)}
 </div>
 <Button
 variant="link"
 className="text-terra hover:text-terra/80"
 onClick={() => dispatch({ type: 'REMOVE', id: item.id })}
 >
 Remove
 </Button>
 </div>
 </li>
 );
 })}
 </ul>
 </div>

 {/* Summary */}
 <aside className="rounded-lg border border-blush/60 bg-white p-6">
 <h2 className="font-headline text-xl text-charcoal">Order Summary</h2>

 <div className="mt-4 space-y-2 text-sm">
 <div className="flex justify-between">
 <span className="text-charcoal/80">Subtotal</span>
 <span className="font-medium text-charcoal">{formatMoney(subtotal, currency)}</span>
 </div>
 <div className="flex justify-between">
 <span className="text-charcoal/80">Shipping</span>
 <span className="font-medium text-charcoal">{formatMoney(shipping, currency)}</span>
 </div>
 </div>

 <Separator className="my-4" />

 <div className="flex items-center justify-between text-lg font-semibold text-charcoal">
 <span>Total</span>
 <span>{formatMoney(total, currency)}</span>
 </div>

 {missingFormat ? (
 <p role="alert" className="mt-4 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
 Every book needs an EPUB or PDF selection. Open the title page to choose a format if this is an older saved cart.
 </p>
 ) : mixedCurrency ? (
 <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
 Please checkout one currency at a time. Remove either EUR or USD items before continuing.
 </p>
 ) : (
 <Button asChild size="lg" className="mt-6 w-full bg-terra text-white hover:bg-terra/90">
 <Link href="/checkout">Proceed to Checkout</Link>
 </Button>
 )}

 <p className="mt-3 text-center text-xs text-charcoal/70">Your chosen format is revalidated against the live catalog before Stripe checkout opens.</p>

 <div className="mt-6 text-center text-sm">
 <Link href="/shop" className="text-terra underline underline-offset-2 hover:text-terra/90">
  Continue shopping
 </Link>
 </div>
 </aside>
 </div>
 );
}
